import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button, Dialog, InlineMessage } from "../ui";
import { AuthError, onUnauthenticated, rawSend, setReauthenticationHandler } from "./api";
import { passkeyCancelled, reauthenticateWithPasskey, signInWithPasskey } from "./passkeys";
import type { SessionInfo } from "./types";

type SessionState = { status: "loading" } | { status: "authenticated"; info: SessionInfo } | { status: "unauthenticated" };

type SessionContextValue = {
  session: SessionState;
  signIn: () => Promise<void>;
  adopt: (info: SessionInfo) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  reauthenticate: () => Promise<boolean>;
};

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

type SessionResponse = SessionInfo | { operator: null; token: true };

function isOperatorSession(value: SessionResponse): value is SessionInfo {
  return value.operator !== null;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [session, setSession] = useState<SessionState>({ status: "loading" });
  const [reauthOpen, setReauthOpen] = useState(false);
  const [reauthBusy, setReauthBusy] = useState(false);
  const [reauthError, setReauthError] = useState<string | null>(null);
  const pending = useRef<((confirmed: boolean) => void) | null>(null);

  const refresh = useCallback(async () => {
    try {
      const response = await rawSend<SessionResponse>("GET", "/admin/auth/session");
      setSession(isOperatorSession(response) ? { status: "authenticated", info: response } : { status: "unauthenticated" });
    } catch (error) {
      if (error instanceof AuthError) setSession({ status: "unauthenticated" });
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    const stop = onUnauthenticated(() => setSession({ status: "unauthenticated" }));
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      stop();
    };
  }, [refresh]);

  const reauthenticate = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        pending.current?.(false);
        pending.current = resolve;
        setReauthError(null);
        setReauthOpen(true);
      }),
    [],
  );

  useEffect(() => {
    setReauthenticationHandler(reauthenticate);
    return () => setReauthenticationHandler(null);
  }, [reauthenticate]);

  const finishReauth = (confirmed: boolean) => {
    pending.current?.(confirmed);
    pending.current = null;
    setReauthOpen(false);
  };

  const confirmWithPasskey = async () => {
    setReauthBusy(true);
    setReauthError(null);
    try {
      const info = await reauthenticateWithPasskey();
      setSession({ status: "authenticated", info });
      finishReauth(true);
    } catch (error) {
      setReauthError(passkeyCancelled(error) ? t("auth.reauth.cancelled") : t("auth.reauth.failed"));
    } finally {
      setReauthBusy(false);
    }
  };

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      signIn: async () => {
        const info = await signInWithPasskey();
        setSession({ status: "authenticated", info });
      },
      adopt: (info) => setSession({ status: "authenticated", info }),
      signOut: async () => {
        try {
          await rawSend("POST", "/admin/auth/sign-out");
        } finally {
          setSession({ status: "unauthenticated" });
        }
      },
      refresh,
      reauthenticate,
    }),
    [session, refresh, reauthenticate],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
      <Dialog
        open={reauthOpen}
        onOpenChange={(open) => {
          if (!open) finishReauth(false);
        }}
        title={t("auth.reauth.title")}
        description={t("auth.reauth.description")}
        size="sm"
        footer={
          <>
            <Button variant="subtle" onClick={() => finishReauth(false)}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" loading={reauthBusy} onClick={() => void confirmWithPasskey()}>
              {t("auth.reauth.confirm")}
            </Button>
          </>
        }
      >
        {reauthError ? <InlineMessage tone="danger">{reauthError}</InlineMessage> : null}
      </Dialog>
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside SessionProvider");
  return value;
}

export function useOperator() {
  const { session } = useSession();
  return session.status === "authenticated" ? session.info.operator : null;
}

export function useCanEdit(): boolean {
  return useOperator()?.role === "admin";
}
